/**
 * DetailsModal - Correção Definitiva CDR Group
 * 
 * ABORDAGEM: Ignora completamente o atributo [open] do <details>.
 * Controla visibilidade APENAS via classe CSS .is-open no elemento <details-modal>.
 * Isso elimina 100% dos conflitos com o toggle nativo do navegador.
 */
class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this._isOpen = false;

    /* =====================================================
       PASSO 1: MATAR o comportamento nativo do <details>
       ===================================================== */

    // Intercepta o clique no summary ANTES de qualquer coisa (capture phase)
    this.summaryToggle.addEventListener(
      'click',
      (e) => {
        // Impede TUDO: propagação, default, outros listeners
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this._handleToggle();
      },
      true
    );

    // Se o browser abrir o <details> nativamente (bug Chrome/Safari),
    // fecha imediatamente para que o [open] não interfira
    this.detailsContainer.addEventListener('toggle', () => {
      if (this.detailsContainer.open && !this._isOpen) {
        // O browser abriu sem a gente pedir — fecha silenciosamente
        this.detailsContainer.open = false;
      }
    });

    // Botão X de fechar
    const closeBtn = this.querySelector('button[type="button"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    this.summaryToggle.setAttribute('role', 'button');
  }

  /* =====================================================
     PASSO 2: Toggle controlado apenas por classe CSS
     ===================================================== */

  _handleToggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this._isOpen) return;
    this._isOpen = true;

    // Fecha o menu drawer se estiver aberto
    this._closeMenuDrawer();

    // Sinaliza globalmente
    document.documentElement.setAttribute('data-search-modal-open', 'true');

    // CLASSE CSS controla a visibilidade (não o [open])
    this.classList.add('is-open');

    // Também seta open no details para manter acessibilidade
    this.detailsContainer.open = true;

    document.body.classList.add('overflow-hidden');

    // Foca no input
    requestAnimationFrame(() => {
      const input = this.detailsContainer.querySelector('input:not([type="hidden"])');
      if (input) input.focus({ preventScroll: true });
    });

    // Body click para fechar ao clicar fora — com delay seguro
    this._bodyClickHandler = this._bodyClickHandler || ((e) => this._onBodyClick(e));
    setTimeout(() => {
      document.addEventListener('click', this._bodyClickHandler, true);
    }, 300);
  }

  close(focusToggle = true) {
    if (!this._isOpen) return;
    this._isOpen = false;

    document.documentElement.removeAttribute('data-search-modal-open');

    // Remove classe CSS
    this.classList.remove('is-open');

    // Remove open do details
    this.detailsContainer.open = false;

    document.removeEventListener('click', this._bodyClickHandler, true);
    document.body.classList.remove('overflow-hidden');

    if (focusToggle && this.summaryToggle) {
      this.summaryToggle.focus({ preventScroll: true });
    }
  }

  // Callback para clique fora do modal
  _onBodyClick(e) {
    // Ignora cliques no próprio summary
    if (this.summaryToggle.contains(e.target)) return;
    // Ignora cliques dentro do modal (exceto overlay)
    if (this.contains(e.target) && !e.target.classList.contains('modal-overlay')) return;
    // Fecha
    this.close(false);
  }

  // Fecha qualquer menu drawer que esteja aberto
  _closeMenuDrawer() {
    const headerDrawer = document.querySelector('header-drawer');
    if (!headerDrawer) return;

    const details = headerDrawer.querySelector('details');
    if (details && details.open) {
      details.open = false;
      details.classList.remove('menu-opening');
      const header = document.querySelector('.section-header');
      if (header) header.classList.remove('menu-open');
      document.body.className = document.body.className
        .replace(/overflow-hidden-\w+/g, '')
        .trim();
    }
  }

  // Método público para compatibilidade
  isOpen() {
    return this._isOpen;
  }
}

customElements.define('details-modal', DetailsModal);